import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Mail, Loader2 } from 'lucide-react';
import {
  OUTCOMES, labelFor, colorFor, todayISO, addDaysISO, showDay, EmptyMsg, apiError,
} from './salesShared';

const firstOfMonth = () => `${todayISO().slice(0, 8)}01`;
const PERIODS = [
  { key: '7', label: 'Last 7 days', from: () => addDaysISO(-6) },
  { key: '30', label: 'Last 30 days', from: () => addDaysISO(-29) },
  { key: 'month', label: 'This month', from: firstOfMonth },
];

// Manager scoreboard: one row per sales rep.
const TeamTab = ({ onPickRep }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [period, setPeriod] = useState('7');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const from = PERIODS.find(p => p.key === period).from();
      setData(await api.get(`/sales/scoreboard?date_from=${from}&date_to=${todayISO()}&today=${todayISO()}`));
    } catch (e) {
      setError(apiError(e, 'Could not load the team view'));
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const sendDigest = async () => {
    if (!window.confirm('Email each sales rep their follow-ups for today now?')) return;
    setSending(true);
    setSendResult('');
    try {
      const res = await api.post(`/sales/digest/send-now?today=${todayISO()}`, {});
      const parts = [`Sent to ${res.sent.length}`];
      if (res.skipped.length) parts.push(`${res.skipped.length} skipped (nothing due, or no email on the rep)`);
      if (res.failed.length) parts.push(`${res.failed.length} failed (check Settings → Email Settings)`);
      setSendResult(parts.join(' · '));
    } catch (e) {
      setSendResult(apiError(e, 'Could not send'));
    } finally {
      setSending(false);
    }
  };

  const rows = data?.rows || [];
  const sum = (k) => rows.reduce((n, r) => n + (r[k] || 0), 0);
  const th = 'text-right px-3 py-2 whitespace-nowrap';
  const td = 'px-3 py-2 text-right font-mono';

  return (
    <div className="space-y-4" data-testid="team-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1">
          {PERIODS.map(p => (
            <button
              key={p.key} type="button" onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 text-sm rounded-md border ${period === p.key ? 'border-[#3B82F6] text-[#F8FAFC] bg-[#1E293B]' : 'border-[#1E293B] text-[#94A3B8] hover:text-[#F8FAFC]'}`}
              data-testid={`team-period-${p.key}`}
            >{p.label}</button>
          ))}
        </div>
        {isAdmin && (
          <div className="flex items-center gap-3">
            {sendResult && <span className="text-xs text-[#94A3B8]" data-testid="digest-result">{sendResult}</span>}
            <Button size="sm" variant="outline" onClick={sendDigest} disabled={sending} className="border-[#1E293B] bg-[#121212] text-[#94A3B8] hover:text-[#F8FAFC]" data-testid="send-digest-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Email today&apos;s follow-ups
            </Button>
          </div>
        )}
      </div>

      {error && <div className="text-sm text-red-400">{error}</div>}
      {data && (
        <p className="text-sm text-[#94A3B8]" data-testid="team-summary">
          {showDay(data.date_from)} – {showDay(data.date_to)} · {data.total_clients.toLocaleString()} clients,
          {' '}{data.unassigned_clients.toLocaleString()} not assigned to anyone yet.
        </p>
      )}

      <div className="bg-[#121212] border border-[#1E293B] rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="team-table">
          <thead className="bg-[#0A0A0A] text-[#94A3B8] text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Sales rep</th>
              <th className={th} title="Clients assigned to this rep">Clients</th>
              <th className={th} title="Activities logged in the period">Activities</th>
              <th className={th}>Calls</th>
              <th className={th} title="Different clients contacted in the period">Clients reached</th>
              <th className={th}>Proposals</th>
              <th className={th}>Won</th>
              <th className={th}>Due today</th>
              <th className={th}>Overdue</th>
              <th className="text-left px-3 py-2">Their clients by status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.sales_rep_id} className="border-t border-[#1E293B]" data-testid={`team-row-${r.sales_rep_id}`}>
                <td className="px-3 py-2">
                  {onPickRep && r.sales_rep_id !== '__other__'
                    ? <button type="button" onClick={() => onPickRep(r.sales_rep_id)} className="text-[#F8FAFC] hover:text-[#3B82F6]" title="Show this rep's day">{r.sales_rep_name}</button>
                    : <span className="text-[#94A3B8]">{r.sales_rep_name}</span>}
                  <div className="text-xs text-[#475569]">{r.last_activity_at ? `Last activity ${showDay(r.last_activity_at)}` : 'No activity in this period'}</div>
                </td>
                <td className={`${td} text-[#F8FAFC]`}>{r.clients.toLocaleString()}</td>
                <td className={`${td} text-[#F8FAFC]`}>{r.activities}</td>
                <td className={`${td} text-[#94A3B8]`}>{r.calls}</td>
                <td className={`${td} text-[#94A3B8]`}>{r.clients_contacted}</td>
                <td className={`${td} text-[#A855F7]`}>{r.proposals}</td>
                <td className={`${td} text-[#10B981]`}>{r.won}</td>
                <td className={`${td} text-[#3B82F6]`}>{r.followups_today}</td>
                <td className={`${td} ${r.followups_overdue ? 'text-red-400' : 'text-[#94A3B8]'}`}>{r.followups_overdue}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {OUTCOMES.filter(o => r.by_status[o.value]).map(o => (
                      <span key={o.value} className="text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap" style={{ backgroundColor: `${colorFor(OUTCOMES, o.value)}22`, color: colorFor(OUTCOMES, o.value) }}>
                        {labelFor(OUTCOMES, o.value)} {r.by_status[o.value]}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length > 1 && (
              <tr className="border-t-2 border-[#1E293B] bg-[#0A0A0A]" data-testid="team-totals">
                <td className="px-3 py-2 text-[#94A3B8] text-xs uppercase">Total</td>
                <td className={`${td} text-[#F8FAFC]`}>{sum('clients').toLocaleString()}</td>
                <td className={`${td} text-[#F8FAFC]`}>{sum('activities')}</td>
                <td className={`${td} text-[#94A3B8]`}>{sum('calls')}</td>
                <td className={`${td} text-[#94A3B8]`}>{sum('clients_contacted')}</td>
                <td className={`${td} text-[#A855F7]`}>{sum('proposals')}</td>
                <td className={`${td} text-[#10B981]`}>{sum('won')}</td>
                <td className={`${td} text-[#3B82F6]`}>{sum('followups_today')}</td>
                <td className={`${td} text-red-400`}>{sum('followups_overdue')}</td>
                <td />
              </tr>
            )}
          </tbody>
        </table>
        {loading && rows.length === 0 && <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#3B82F6]" /></div>}
        {!loading && rows.length === 0 && <EmptyMsg text="No sales reps yet. Add them in Settings → Sales Reps." />}
      </div>
    </div>
  );
};

export default TeamTab;
